import React, { useState, useEffect } from "react";
import { X, ShieldCheck } from "lucide-react";
import { safeLocalStorage } from "../lib/safeStorage";

interface CookieBannerProps {
  onOpenPrivacyPolicy: () => void;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({ onOpenPrivacyPolicy }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [showPreferences, setShowPreferences] = useState<boolean>(false);
  const [allowHistory, setAllowHistory] = useState<boolean>(true);

  useEffect(() => {
    try {
      const consent = safeLocalStorage.getItem("catchai_cookie_consent") || safeLocalStorage.getItem("veritext_cookie_consent");
      if (!consent) {
        // Small delay for smooth, polite entrance after initial page paint
        const timer = setTimeout(() => setIsVisible(true), 900);
        return () => clearTimeout(timer);
      }
    } catch {
      // ignore
    }
  }, []);

  const handleAcceptAll = () => {
    try {
      const payload = JSON.stringify({
        essential: true,
        localHistory: true,
        timestamp: new Date().toISOString()
      });
      safeLocalStorage.setItem("catchai_cookie_consent", payload);
      safeLocalStorage.setItem("veritext_cookie_consent", payload);
    } catch {}
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    try {
      const payload = JSON.stringify({
        essential: true,
        localHistory: false,
        timestamp: new Date().toISOString()
      });
      safeLocalStorage.setItem("catchai_cookie_consent", payload);
      safeLocalStorage.setItem("veritext_cookie_consent", payload);
    } catch {}
    setIsVisible(false);
  };

  const handleSaveCustom = () => {
    try {
      const payload = JSON.stringify({
        essential: true,
        localHistory: allowHistory,
        timestamp: new Date().toISOString()
      });
      safeLocalStorage.setItem("catchai_cookie_consent", payload);
      safeLocalStorage.setItem("veritext_cookie_consent", payload);
    } catch {}
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie & Data Privacy Notice"
      className="fixed bottom-3 inset-x-3 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm z-45 animate-fade-in pointer-events-auto"
      id="catchai-cookie-consent-banner"
    >
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200/90 dark:border-slate-800 rounded-2xl p-3.5 sm:p-4 shadow-xl flex flex-col gap-2.5 transition-all text-xs">
        
        {/* Header and message */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-3.5 h-3.5" />
            </div>
            <span className="font-semibold text-xs text-slate-900 dark:text-slate-100">
              Privacy & Local Storage
            </span>
          </div>

          <button
            type="button"
            onClick={handleRejectNonEssential}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1 -mr-1 -mt-0.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer shrink-0"
            title="Dismiss"
            aria-label="Close cookie notice"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          CatchAI Text saves scan history and preferences locally on your device. We use zero tracking or advertising cookies.{" "}
          <button
            type="button"
            onClick={() => onOpenPrivacyPolicy()}
            className="text-slate-900 dark:text-slate-200 underline hover:no-underline font-medium cursor-pointer"
          >
            Privacy Policy
          </button>
        </p>

        {/* Detailed preference drawer */}
        {showPreferences && (
          <div className="border-t border-slate-100 dark:border-slate-800 pt-2.5 flex flex-col gap-2 animate-fade-in text-[11px]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="font-medium text-slate-800 dark:text-slate-200 block">Strictly Necessary</span>
                <span className="text-slate-500 dark:text-slate-400">Core analysis, theme, and language.</span>
              </div>
              <span className="font-semibold text-slate-600 dark:text-slate-300 shrink-0 text-[10px]">
                Required
              </span>
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800/60">
              <div>
                <span className="font-medium text-slate-800 dark:text-slate-200 block">Local Scan History</span>
                <span className="text-slate-500 dark:text-slate-400">Stores past scans in browser memory.</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={allowHistory}
                  onChange={(e) => setAllowHistory(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-7 h-4 bg-slate-300 peer-focus:outline-hidden rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-slate-900 dark:peer-checked:bg-white"></div>
              </label>
            </div>
          </div>
        )}

        {/* Browser-style actions row */}
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={() => setShowPreferences(!showPreferences)}
            className="text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer"
          >
            {showPreferences ? "Fewer options" : "Manage"}
          </button>

          <div className="flex items-center gap-1.5 ml-auto">
            {showPreferences ? (
              <button
                type="button"
                onClick={handleSaveCustom}
                className="py-1 px-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-medium transition-colors cursor-pointer"
              >
                Save
              </button>
            ) : (
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="py-1 px-2.5 sm:px-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-medium transition-colors cursor-pointer"
              >
                Decline
              </button>
            )}

            <button
              type="button"
              onClick={handleAcceptAll}
              className="py-1 px-3.5 rounded-lg bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium transition-colors cursor-pointer shadow-xs"
            >
              Accept
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
